import { ART_URL, apiGetCall } from '@/api/apiDispatcher';
import { startTransition, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';
import HmltToRender from '@/utils/HtmlToRender';
import { appDataContext } from '@/context';
import ArticleSSRContext from '@/context/ArticleSSRContext';
import { getSSRArticle, getSSRArticleSlug } from '@/store/article-ssr';
import styled from 'styled-components';

const SITE_URL = 'https://mazout.com';

const toAbsoluteUrl = (src) =>
  src ? (src.startsWith('http') ? src : `${SITE_URL}${src}`) : null;

const toPlainText = (html) => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '').trim();
  return text.length > 160 ? text.slice(0, 157) + '...' : text;
};

const Article = ({ lang, url, hideImage, className }) => {
  const ssrCtx = useContext(ArticleSSRContext);
  const dataContext = useContext(appDataContext);
  const dataLang = dataContext?.lang;
  const navigateTo = useNavigate();
  const location = useLocation();

  // Initialize from SSR data if available for this exact URL — avoids a client-side
  // fetch on first load and ensures the SSR HTML matches the hydrated render.
  const [data, setData] = useState(() => {
    if (ssrCtx?.articleSlug === url && ssrCtx.article) return ssrCtx.article;
    if (getSSRArticleSlug() === url) return getSSRArticle(url);
    return null;
  });

  // Track which URL the current data was loaded for to handle in-app navigation.
  const loadedUrl = useRef(data ? url : null);

  useEffect(() => {
    if (url === undefined) return;
    if (loadedUrl.current === url) return; // Already have data for this URL

    setData(null);
    loadedUrl.current = null;

    apiGetCall(ART_URL + '/' + url, lang)
      .then((response) => {
        startTransition(() => {
          if (response?.data?.id) {
            setData(response.data);
            loadedUrl.current = url;
          } else {
            navigateTo('/404', { state: {}, replace: true });
          }
        });
      })
      .catch(() => {
        startTransition(() => {
          navigateTo('/404', { state: {}, replace: true });
        });
      });
  }, [url, lang, navigateTo]);

  const titleToRender = data ? `${data.title} - ${dataLang?.site.title}` : '';
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  const ogImage = toAbsoluteUrl(data?.media?.src);
  const description = toPlainText(data?.excerpt);
  const ogLocale = lang ? lang.replace('-', '_') : 'fr_BE';

  const articleStructuredData = data
    ? {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        headline: data.title,
        url: canonicalUrl,
        image: ogImage,
        description,
        datePublished: data.created?.datetime,
        dateModified: data.updated?.datetime,
        articleSection: data.categories?.[0]?.title,
        author: {
          '@type': 'Organization',
          name: 'Mazout.com',
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Mazout.com',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/images/logo.svg`,
          },
        },
      }
    : null;

  const formattedDate = useMemo(() => {
    const dateStr = data?.created?.datetime;
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-BE');
    } catch (e) {
      return '';
    }
  }, [data?.created?.datetime]);

  return (
    <ContentWrapper className={className}>
      {data && (
        <Helmet encodeSpecialCharacters={false}>
          <title>{titleToRender}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href={canonicalUrl} />

          <meta property="og:type" content="article" />
          <meta property="og:title" content={titleToRender} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:site_name" content="Mazout.com" />
          <meta property="og:locale" content={ogLocale} />
          {ogImage && <meta property="og:image" content={ogImage} />}
          {data.created?.datetime && (
            <meta property="article:published_time" content={data.created.datetime} />
          )}
          {data.updated?.datetime && (
            <meta property="article:modified_time" content={data.updated.datetime} />
          )}
          {data.categories?.[0]?.title && (
            <meta property="article:section" content={data.categories[0].title} />
          )}
          <meta property="article:author" content="Mazout.com" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={titleToRender} />
          <meta name="twitter:description" content={description} />
          {ogImage && <meta name="twitter:image" content={ogImage} />}

          <script type="application/ld+json">{JSON.stringify(articleStructuredData)}</script>
        </Helmet>
      )}

      {!data ? (
        <div style={{ minHeight: '200px' }}></div>
      ) : (
        <>
          {!hideImage && (
            <header>
              <h1>{data.title}</h1>
              <Created>{formattedDate}</Created>
            </header>
          )}
          {!hideImage && data.media?.src && (
            <img
              src={data.media.src}
              alt={data.media.alt}
              loading='eager'
              fetchPriority='high'
              width={1000}
              height={500}
            />
          )}
          <div>{data.content && <HmltToRender __html={data.content} />}</div>
        </>
      )}
    </ContentWrapper>
  );
};

export default Article;

const ContentWrapper = styled.article.attrs({
  className: 'article ContentWrapper',
})`
  div,
  p {
    line-height: 1.6;
    font-size: 1.6rem;
    margin-bottom: 12px;
    color: ${(props) => props.theme.colors.dark};
  }
  a {
    text-decoration: underline;
    color: ${(props) => props.theme.colors.blue};
  }
  ul {
    padding-left: 20px;
    margin-bottom: 16px;
    list-style-type: none;
    li {
      white-space: normal;
    }
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 27px;
    margin-bottom: 12px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  li {
    white-space: pre-wrap;
  }

  header {
    background-color: #fefefe;
    border-radius: 4px 4px 0 0;
    margin: 1rem auto 2rem auto;
    border-bottom: 1px solid ${(props) => props.theme.colors.grey};
    padding: 0rem auto;
    position: relative;
    text-align: center;
    h1 {
      font-weight: 400;
    }
  }
`;

const Created = styled.div`
  color: ${(props) => props.theme.colors.gray};
  font-size: 1.4rem;
  margin-bottom: 2rem;
`;
